
class TimeoutManager {
  private timeouts = new Map<string, NodeJS.Timeout>();
  private intervals = new Map<string, NodeJS.Timeout>();
  private retryCounters = new Map<string, number>();
  private maxRetries = 3;

  // Set a timeout with automatic cleanup
  setTimeout(key: string, callback: () => void, delay: number): void {
    this.clearTimeout(key);
    const timeout = setTimeout(() => {
      this.timeouts.delete(key);
      callback();
    }, delay);
    this.timeouts.set(key, timeout);
  }

  // Set an interval with automatic cleanup
  setInterval(key: string, callback: () => void, delay: number): void {
    this.clearInterval(key);
    const interval = setInterval(callback, delay);
    this.intervals.set(key, interval);
  }

  // Set a retry timeout with exponential backoff and max retries
  setRetry(key: string, callback: () => void, baseDelay: number = 1000): boolean {
    const retryCount = this.retryCounters.get(key) || 0;
    
    if (retryCount >= this.maxRetries) {
      console.warn(`Max retries reached for ${key}`);
      this.retryCounters.delete(key);
      return false;
    }

    this.retryCounters.set(key, retryCount + 1);
    const delay = Math.min(baseDelay * Math.pow(2, retryCount), 10000); // Cap at 10s
    
    this.setTimeout(`retry-${key}`, () => {
      callback();
    }, delay);

    return true;
  }

  // Reset retry counter on success
  resetRetries(key: string): void {
    this.retryCounters.delete(key);
  }

  // Clear specific timeout
  clearTimeout(key: string): void {
    const timeout = this.timeouts.get(key);
    if (timeout) {
      clearTimeout(timeout);
      this.timeouts.delete(key);
    }
  }

  // Clear specific interval
  clearInterval(key: string): void {
    const interval = this.intervals.get(key);
    if (interval) {
      clearInterval(interval);
      this.intervals.delete(key);
    }
  }

  // Clear all timeouts and intervals
  clearAll(): void {
    this.timeouts.forEach(timeout => clearTimeout(timeout));
    this.intervals.forEach(interval => clearInterval(interval));
    this.timeouts.clear();
    this.intervals.clear();
    this.retryCounters.clear();
  }

  // Get active timeout/interval count for debugging
  getActiveCount(): { timeouts: number; intervals: number; retries: number } {
    return {
      timeouts: this.timeouts.size,
      intervals: this.intervals.size,
      retries: this.retryCounters.size
    };
  }
}

export const timeoutManager = new TimeoutManager();
